import { createContext, useState } from 'react'

import { api } from 'utils/api'
import { getError } from 'utils/helpers/getError'
import { useCustomContext } from 'utils/helpers/useCustomContext'
import { GenericResponse, GenericStateType } from 'utils/types'

import {
  PaymentCardType,
  PaymentContextType,
  PaymentCreateSubscription,
  PaymentCreateType,
  PaymentOptionsType,
  PaymentStateType,
  PaymentSubscriptionInfoType,
  PaymentSubscriptionResultType
} from './paymentTypes'

const INITIAL_PAYMENT_CONTEXT: PaymentContextType = {
  setLoading: () => null,
  setError: () => null,
  clearError: () => null,
  clearContent: () => null,
  createPayment: () => new Promise(() => null),
  cancelSubscription: () => new Promise(() => null),
  getCurrentSubscriptionByVehicleId: () => new Promise(() => null),
  getPaymentOptions: () => new Promise(() => null),
  // processPayment: () => new Promise(() => null),
  checkClientCard: () => new Promise(() => null),
  createSubscription: () => new Promise(() => null),
  getInstallationPaymentStatus: () => new Promise(() => null),
  paymentState: {
    error: '',
    loading: false,
    state: {
      subscriptionResult: {
        datosCliente: '',
        pagoRecurrente: '',
        token: '',
        url: '',
        xml: '',
        kaufmannPaymentUrl: ''
      },
      paymentOptions: {
        error: '',
        loading: false,
        data: {
          anualPayment: {},
          firstPayment: {},
          onlyRenew: {},
          installation: {}
        }
      },
      currentSubscription: null,
      isPaidInstallation: false,
      cardOptions: {
        error: '',
        loading: false,
        data: null,
      },
    },
  },
}

const PaymentContext = createContext<PaymentContextType>(INITIAL_PAYMENT_CONTEXT)

const PaymentProvider: React.FC = ({ children }) => {
  const [paymentState, setPaymentState] = useState<
    GenericStateType<PaymentStateType>
  >(INITIAL_PAYMENT_CONTEXT.paymentState)

  const setLoading = (value = true) =>
    setPaymentState(prevState => ({ ...prevState, loading: value }))

  const setPaymentOptionsLoading = (value = true) =>
    setPaymentState(prevState => ({
      ...prevState,
      state: { ...prevState.state, paymentOptions: { ...prevState.state.paymentOptions, loading: value } }
    }))
  const setPaymentOptionsError = (msg: string) =>
    setPaymentState(prevState => ({
      ...prevState,
      state: { ...prevState.state, paymentOptions: { ...prevState.state.paymentOptions, error: msg } }
    }))

  const setCardOptionsLoading = (value = true) =>
    setPaymentState(prevState => ({
      ...prevState,
      state: { ...prevState.state, cardOptions: { ...prevState.state.cardOptions, loading: value } },
    }))

  const setCardOptionsError = (msg: string) =>
    setPaymentState(prevState => ({
      ...prevState,
      state: { ...prevState.state, cardOptions: { ...prevState.state.cardOptions, error: msg } },
    }))

  const setError = (msg: string) =>
    setPaymentState(prevState => ({ ...prevState, loading: false, error: msg }))

  const clearError = () =>
    setPaymentState(prevState => ({ ...prevState, error: '' }))

  const clearContent = () =>
    setPaymentState(prevState => ({
      ...prevState,
      error: '',
      loading: false,
      state: {
        ...prevState.state,
        subscriptionResult: {
          datosCliente: '',
          pagoRecurrente: '',
          token: '',
          url: '',
          xml: '',
          kaufmannPaymentUrl: ''
        }
      },
    }))

  const createPayment = async (data: PaymentCreateType) => {
    try {
      setLoading()

      const { data: { payload } } =
        await api.post<GenericResponse<PaymentSubscriptionResultType>>('OneClick/CreatePayment', data)

      setLoading(false)
      setError('')

      return payload
    } catch (err) {
      const msg = getError(err)

      setError(msg)

      return null
    }
  }

  const getCurrentSubscriptionByVehicleId = async (vehicleId: string) => {
    try {
      setLoading()

      const { data: { payload } } =
        await api.get<GenericResponse<PaymentSubscriptionInfoType>>(`OneClick/${vehicleId}/VehiclePayments`)

      setPaymentState(prevState =>
        ({ ...prevState, loading: false, error: '', state: { ...prevState.state, currentSubscription: payload } }))

      return true
    } catch (err) {
      const msg = getError(err)

      setError(msg)

      return false
    }
  }

  const getInstallationPaymentStatus = async (vehicleId: string) => {
    try {
      setLoading()

      const { data: { payload } } =
        await api.get<GenericResponse<boolean>>(`OneClick/${vehicleId}/InstallationPaymentStatus`)

      setPaymentState(prevState =>
          ({ ...prevState, loading: false, error: '', state: { ...prevState.state, isPaidInstallation: payload } }))

      return payload
    } catch (err) {
      const msg = getError(err)

      setError(msg)

      return false
    }
  }

  const cancelSubscription = async (vehicleId: string) => {
    try {
      const { data:
        { payload, message },
      } = await api.post<GenericResponse<boolean>>(`OneClick/CancelSubscription/${vehicleId}`)

      return payload ? '' : message
    } catch (err) {
      const msg = getError(err)

      return msg
    }
  }

  const getPaymentOptions = async () => {
    try {
      setPaymentOptionsLoading()

      const { data: { payload } } = await api.get<GenericResponse<PaymentOptionsType>>('OneClick/Prices')

      setPaymentState(prevState =>
        ({ ...prevState, state: { ...prevState.state, paymentOptions: { error: '', loading: false, data: payload } } }))

      return true
    } catch (err) {
      const msg = getError(err)

      setPaymentOptionsError(msg)

      return false
    }
  }

  const checkClientCard = async () => {
    try {
      setCardOptionsLoading()

      const { data: { payload } } = await api.get<GenericResponse<PaymentCardType>>('OneClick/CheckClientCard')

      setPaymentState(prevState => ({
        ...prevState, state: {
          ...prevState.state, cardOptions: { ...prevState.state.cardOptions, loading: false, data: payload },
        }
      }))

      return true
    } catch (err) {
      const msg = getError(err)

      setCardOptionsError(msg)
      setCardOptionsLoading(false)

      return false
    }
  }

  const createSubscription = async (data: PaymentCreateSubscription) => {
    try {
      setLoading()

      await api.post<GenericResponse<boolean>>('OneClick/CreateSubscription', data)

      setLoading(false)

      return true
    } catch (err) {
      const msg = getError(err)

      setError(msg)

      return false
    }
  }

  return (
    <PaymentContext.Provider
      value={{
        paymentState,
        setLoading,
        setError,
        clearError,
        clearContent,
        createPayment,
        cancelSubscription,
        getCurrentSubscriptionByVehicleId,
        getPaymentOptions,
        checkClientCard,
        createSubscription,
        getInstallationPaymentStatus
      }}>
      {children}
    </PaymentContext.Provider>
  )
}

const usePayment = () => useCustomContext(PaymentContext)

export { PaymentProvider, usePayment }
